import {
  DATE_COLUMN_TYPES,
  FilterTypesToDescription,
  NUMBER_COLUMN_TYPES,
  SELECT_COLUMN_TYPES
} from 'components/spreadsheet/constants/const'
import { getCellColour, getRowLevelColour } from 'components/spreadsheet/helpers/colouring'
import { getRowValue, isUIFilterActive } from 'components/spreadsheet/helpers/functions'
import { addThousandSeparator, formatCellText } from 'components/spreadsheet/helpers/format'
import { buildOptionsSet, isColumnReadOnly } from 'components/spreadsheet/helpers/validation'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import {
  adaptColourToTheme,
  adaptFontColourToTheme,
  DEFAULT_HEADER_BACKGROUND_COLOUR,
  DEFAULT_HEADER_TEXT_COLOUR
} from 'helpers/colour'
import { ResolvedTheme } from 'helpers/theme'
import { ICellValue, ITableRow, ITableViewColumn } from 'types'
import {
  AGGREGATES,
  Binding,
  BoundRaw,
  BoundValue,
  CellStyle,
  PageColumn,
  PageData,
  PageRow,
  RequestedBinding,
  isId
} from './runtime'
import { pageAccess } from './access'
import { PageTheme, pageHeader, pageTheme } from './theme'

/**
 * A Custom View reads the same data as the table view: the spreadsheet data context's rows, streamed and
 * then sorted and filtered by the reader's current toolbar settings, and the columns the grid shows.
 */
export function visibleColumns(data: ISpreadsheetData, theme: PageTheme = pageTheme()): PageData['columns'] {
  return visibleViewColumns(data).map((column) => toPageColumn(column, data, theme))
}

export function visibleViewColumns(data: ISpreadsheetData): ITableViewColumn[] {
  const hidden = new Set(data.userConfiguration.hiddenColumns)
  return data.viewDetails.columns.filter((column) => !hidden.has(column.publicId))
}

/** Every option the grid's select offers: those from a source table (for all dependency values), then typed-in ones. */
export function allOptions(column: ITableViewColumn): string[] {
  const cached = column.kindOptions?.tableOptions?.cachedOptions ?? []
  const fromTable = Array.isArray(cached) ? cached : Object.values(cached).flat()
  return [...buildOptionsSet(fromTable, column.kindOptions?.manualOptions ?? [])]
}

// A stored colour someone chose: set, and not the default the app stores for "no colour".
const isChosen = (colour: string | null | undefined, fallback: string) =>
  !!colour && colour.trim().toLowerCase() !== fallback.toLowerCase()

function toPageColumn(column: ITableViewColumn, data: ISpreadsheetData, theme: PageTheme): PageColumn {
  const page: PageColumn = {
    id: column.publicId,
    name: column.name,
    kind: column.kind,
    // As in the grid, a column without its own header colours takes the hub's, and both follow the theme.
    header: {
      ...(column.headerBackgroundColor || column.headerTextColor
        ? pageHeader(column.headerBackgroundColor, column.headerTextColor, theme.mode)
        : { background: theme.headerBackground, text: theme.headerText }),
      // Only a header the owner coloured shows its colours on a page; the rest read as a plain table's.
      custom:
        isChosen(column.headerBackgroundColor, DEFAULT_HEADER_BACKGROUND_COLOUR) ||
        isChosen(column.headerTextColor, DEFAULT_HEADER_TEXT_COLOUR)
    },
    readOnly: Boolean(isColumnReadOnly(column, data.isContributor, data.userConfiguration.rowLockSettings)),
    required: Boolean(column.required || column.validationNoBlanks)
  }
  if (SELECT_COLUMN_TYPES.includes(column.kind)) {
    page.options = allOptions(column)
    const dependencies = column.kindOptions?.tableOptions?.dependencies ?? []
    if (dependencies.length) page.optionsDependOn = dependencies.map((dependency) => dependency.columnId)
  }
  return page
}

/** The grid's rows in its order; `rows` holds the full set and `visibleBitmap` the filter (null = none). */
function visibleRows(data: ISpreadsheetData): ITableRow[] {
  const bitmap = data.visibleBitmap
  return bitmap ? data.rows.filter((_, index) => bitmap[index]) : data.rows
}

// Colour functions start from this background; a style that still has it matched no rule.
const UNSTYLED = 'morta:unstyled'

// Adapted for the reader's theme exactly as the grid paints the cell (useThemedColours).
function toCellStyle(colour: ReturnType<typeof getCellColour>, mode: ResolvedTheme): CellStyle | null {
  if (colour.backgroundColour === UNSTYLED) return null
  const background = adaptColourToTheme(colour.backgroundColour, mode)
  return {
    background,
    text: adaptFontColourToTheme(colour.fontColour, background, mode),
    bold: colour.fontBold,
    italic: colour.fontItalic,
    underline: colour.fontUnderline
  }
}

/** How the view's colour rules (the reader's current ones, saved or not) style a value of a column. */
export function valueStyle(
  data: ISpreadsheetData,
  column: ITableViewColumn,
  value: ICellValue,
  mode: ResolvedTheme = 'light'
): CellStyle | null {
  const rules = data.userConfiguration.colourSettings
  if (rules.length === 0) return null
  return toCellStyle(getCellColour(column, data.columnValuesCount, null, value, rules, false, UNSTYLED), mode)
}

type ViewColumns = Map<string, ITableViewColumn>

function rowStyles(
  row: ITableRow,
  columns: PageData['columns'],
  viewColumns: ViewColumns,
  data: ISpreadsheetData,
  mode: ResolvedTheme
): PageRow['styles'] {
  const rules = data.userConfiguration.colourSettings
  if (rules.length === 0) return undefined
  const rowColour = getRowLevelColour(
    rules,
    row,
    viewColumns,
    data.columnIdToIndex,
    data.columnValuesCount,
    false,
    UNSTYLED,
    data.columnStore
  )
  const styles: Record<string, CellStyle> = {}
  for (const { id } of columns) {
    const value = getRowValue(row, id, data.columnIdToIndex, data.columnStore)
    const style = toCellStyle(
      getCellColour(viewColumns.get(id)!, data.columnValuesCount, row, value, rules, false, UNSTYLED, rowColour),
      mode
    )
    if (style) styles[id] = style
  }
  return Object.keys(styles).length ? styles : undefined
}

/** Turns the grid's rows into the page's, looking the view's columns up once for all of them. */
export function pageRowReader(
  columns: PageData['columns'],
  data: ISpreadsheetData,
  mode: ResolvedTheme = 'light'
): (row: ITableRow) => PageRow {
  const viewColumns: ViewColumns = new Map(data.viewDetails.columns.map((column) => [column.publicId, column]))
  return (row) => {
    const values = Object.fromEntries(
      columns.map((column) => [column.id, getRowValue(row, column.id, data.columnIdToIndex, data.columnStore)])
    )
    const page: PageRow = {
      id: row.publicId,
      values,
      texts: Object.fromEntries(
        columns.map((column) => {
          const viewColumn = viewColumns.get(column.id)
          return [column.id, viewColumn ? formatCellText(viewColumn, values[column.id] as ICellValue) : '']
        })
      )
    }
    const styles = rowStyles(row, columns, viewColumns, data, mode)
    if (styles) page.styles = styles
    return page
  }
}

/** Every row of the view for morta.onData, in one go, with the view's columns, as the grid holds them. */
export function readSnapshot(data: ISpreadsheetData, theme: PageTheme = pageTheme()): PageData {
  const columns = visibleColumns(data, theme)
  const rows = visibleRows(data)
  return {
    columns,
    rows: rows.map(pageRowReader(columns, data, theme.mode)),
    total: rows.length,
    access: pageAccess(data)
  }
}

const AGGREGATE_NAMES: Record<NonNullable<Binding['agg']>, string> = {
  sum: 'Sum',
  avg: 'Average',
  count: 'Count',
  min: 'Lowest',
  max: 'Highest'
}

/** The view's active filters as sentences, e.g. "Status is Open", for where a bound number came from. */
export function describeFilters(data: ISpreadsheetData): string[] {
  return data.userConfiguration.filterSettings.filter(isUIFilterActive).map((filter) => {
    const operator = FilterTypesToDescription[filter.filterType] ?? filter.filterType
    const value = filter.multipleValues?.length ? filter.multipleValues.join(', ') : filter.value
    return [filter.columnName, operator, value].filter(Boolean).join(' ')
  })
}

const countText = (count: number) => addThousandSeparator(String(count))
const rowsText = (count: number) => `${countText(count)} ${count === 1 ? 'row' : 'rows'}`

/**
 * What a page's bound elements show: a cell (column and row) or an aggregate of a column over every row the
 * view holds, after the reader's filters, formatted with the grid's own formatter for that column; or why it
 * can't be shown. Each says where it came from, for the reader who clicks it.
 */
/** A bound element's attributes as Morta can use them, or why it can't: each is checked on its own. */
function checkBinding(requested: RequestedBinding): Binding | { missing: string } {
  if (requested.column !== undefined && !isId(requested.column)) {
    return {
      missing: `data-morta-column="${requested.column}" isn't a column ID. Use a column's id from the columns morta.onData gives.`
    }
  }
  if (requested.row !== undefined && !isId(requested.row)) {
    return { missing: `data-morta-row="${requested.row}" isn't a row ID. Use a row's id from morta.onData's rows.` }
  }
  if (requested.agg !== undefined && !(AGGREGATES as readonly string[]).includes(requested.agg)) {
    return {
      missing: `data-morta-agg="${requested.agg}" isn't something Morta works out. Use ${AGGREGATES.join(', ')}.`
    }
  }
  return requested as Binding
}

export function resolveBindings(data: ISpreadsheetData, requested: RequestedBinding[]): BoundValue[] {
  const rows = visibleRows(data)
  const columnsById = new Map(visibleViewColumns(data).map((column) => [column.publicId, column]))
  const filters = describeFilters(data)
  // A page can bind a thousand elements over a large view, so each column is read once and rows are found by ID.
  const columnValues = new Map<string, ICellValue[]>()
  const valuesOf = (columnId: string) => {
    let values = columnValues.get(columnId)
    if (!values) {
      values = rows.map((row) => getRowValue(row, columnId, data.columnIdToIndex, data.columnStore))
      columnValues.set(columnId, values)
    }
    return values
  }
  let rowsById: Map<string, (typeof rows)[number]> | undefined
  const rowById = (id: string) => {
    rowsById ??= new Map(rows.map((row) => [row.publicId, row]))
    return rowsById.get(id)
  }

  return requested.map((attributes): BoundValue => {
    const binding = checkBinding(attributes)
    if ('missing' in binding) return binding
    const column = binding.column ? columnsById.get(binding.column) : undefined
    if (binding.column && !column) return { missing: `Column ${binding.column} is not in this view.` }

    if (binding.agg === 'count') {
      const count = column ? valuesOf(column.publicId).filter((value) => !isBlank(value)).length : rows.length
      const what = column ? `Count of ${column.name}` : 'Count of rows'
      return {
        text: countText(count),
        value: count,
        source: { summary: `${what} over ${rowsText(rows.length)}`, filters }
      }
    }
    if (!column) return { missing: 'A bound element needs data-morta-column, unless it counts rows.' }

    if (binding.row) {
      const row = rowById(binding.row)
      if (!row) return { missing: `Row ${binding.row} is not in this view, or the filters hide it.` }
      const value = getRowValue(row, column.publicId, data.columnIdToIndex, data.columnStore)
      return {
        text: formatCellText(column, value),
        value: toBoundValue(value),
        source: { summary: `${column.name} in one row`, filters }
      }
    }
    if (!binding.agg) return { missing: `A bound ${column.name} needs data-morta-row or data-morta-agg.` }

    const summary = `${AGGREGATE_NAMES[binding.agg]} of ${column.name} over ${rowsText(rows.length)}`
    const values = valuesOf(column.publicId)
    if (DATE_COLUMN_TYPES.includes(column.kind) && (binding.agg === 'min' || binding.agg === 'max')) {
      const dates = values.filter((value): value is string => typeof value === 'string' && value !== '').sort()
      const value = (binding.agg === 'min' ? dates[0] : dates[dates.length - 1]) ?? null
      return { text: formatCellText(column, value), value, source: { summary, filters } }
    }
    if (!NUMBER_COLUMN_TYPES.includes(column.kind)) {
      return { missing: `${AGGREGATE_NAMES[binding.agg]} needs a number column; ${column.name} isn't one.` }
    }
    const numbers = values.map(Number).filter((value, index) => !isBlank(values[index]) && !Number.isNaN(value))
    const result = aggregate(binding.agg, numbers)
    // An average of whole numbers keeps a decimal place, rather than rounding to the column's own.
    const format =
      binding.agg === 'avg' && column.kind === 'integer'
        ? { ...column, kind: 'float' as const, decimalPlaces: Math.max(1, column.decimalPlaces ?? 0) }
        : column
    return { text: result === null ? '' : formatCellText(format, result), value: result, source: { summary, filters } }
  })
}

const isBlank = (value: ICellValue) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

function aggregate(kind: 'sum' | 'avg' | 'min' | 'max', numbers: number[]): number | null {
  if (kind === 'sum') return numbers.reduce((total, value) => total + value, 0)
  if (numbers.length === 0) return null
  if (kind === 'avg') return numbers.reduce((total, value) => total + value, 0) / numbers.length
  // Reduced rather than spread: a spread of a large column overflows the stack.
  return numbers.reduce((best, value) => (kind === 'min' ? Math.min(best, value) : Math.max(best, value)))
}

// A cell's value for the page alongside its text: lists and objects as JSON, the rest as stored.
const toBoundValue = (value: ICellValue): BoundRaw =>
  Array.isArray(value) || (typeof value === 'object' && value !== null) ? JSON.stringify(value) : (value ?? null)
